import { TFunction } from "i18next";
import { ProviderContext } from "notistack";
import { ServiceData } from "../ScheduleSelection-Tab-Services.component";

/**
 * Function to parse validation error messages and show appropriate snackbar
 * @example "Validation errors: Service 165: Cannot change ordered quantity within booking lead time; Service 166: Cannot change ordered quantity within booking lead time"
 */
export const handleValidationError = ({
  cateringData,
  equipmentData,
  layoutData,
  errorMessage,
  enqueueSnackbar,
  t
}: {
  cateringData: ServiceData[];
  equipmentData: ServiceData[];
  layoutData: ServiceData[];
  errorMessage: string;
  enqueueSnackbar: ProviderContext["enqueueSnackbar"];
  t: TFunction;
}) => {
  // Parse the error message to extract service IDs and error types
  const serviceErrorRegex = /Service (\d+): ([^;]+)/g;
  const matches = [...errorMessage.matchAll(serviceErrorRegex)];

  const showErrorSnackbar = (errMsg: string) => {
    return enqueueSnackbar(t(errMsg), { variant: "error" });
  };

  // Get service names from available data
  const getServiceName = (serviceId: number): string => {
    // Check in catering data
    const cateringItem = cateringData.find(item => item.id === serviceId);
    if (cateringItem?.name) return cateringItem.name;

    // Check in equipment data
    const equipmentItem = equipmentData.find(item => item.id === serviceId);
    if (equipmentItem?.name) return equipmentItem.name;

    // Check in layout data
    const layoutItem = layoutData.find(item => item.id === serviceId);
    if (layoutItem?.name) return layoutItem.name;

    // Fallback to service ID if name not found
    return `Service ${serviceId}`;
  };

  if (matches.length > 0) {
    // Extract service IDs and error messages
    const serviceErrors = matches.map(match => ({
      serviceId: Number.parseInt(match[1]),
      errorMessage: match[2].trim()
    }));

    // Check if it's a booking lead time error
    const isBookingLeadTimeError = serviceErrors.some(error =>
      error.errorMessage.includes("Cannot change ordered quantity within booking lead time")
    );

    // Find the matched service item's name by serviceId
    const serviceNames = serviceErrors.map(error => getServiceName(error.serviceId)).join(", ");
    if (isBookingLeadTimeError) {
      // Show translated booking lead time error
      showErrorSnackbar(
        t("Cannot change ordered quantity within booking lead time") + ` (${serviceNames})`
      );
    } else {
      // Show generic error with service names
      showErrorSnackbar(t("Error while editing your booking") + ` (${serviceNames})`);
    }
  } else {
    // Fallback to generic error message
    showErrorSnackbar(t("Error while editing your booking"));
  }
};
